/*eslint-disable max-lines*/
import { useSearchForensics } from '@hakimo-ui/hakimo/data-access';
import {
  ForensicsFilterValues,
  ForensicsSearchAlarms,
} from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { withAuthz, withErrorBoundary } from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ForensicsAlarmsSection } from './ForensicsAlarmsSection';
import { ForensicsFilterSection } from './ForensicsFilterSection';
import { ForensicsMessageSection } from './ForensicsMessageSection';
import { buildForensicsPayload } from './util';

interface SearchQuery {
  inputValue: string;
  filters?: ForensicsFilterValues;
  selectedImage?: File;
}

const ForensicsRootComponent: FC = () => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const initialUrlSearchParamsRef = useRef<URLSearchParams>();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>(() => {
    initialUrlSearchParamsRef.current = urlSearchParams;
    return {
      inputValue: urlSearchParams.get('q') || '',
    };
  });
  const [results, setResults] = useState<ForensicsSearchAlarms[]>();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const getSearchResults = useSearchForensics();
  const itemQueryCountRef = useRef(9);

  const updateUrlSearchParams = (newSearchQuery: SearchQuery) => {
    const newUrlSearchParams = new URLSearchParams();
    newUrlSearchParams.set('q', newSearchQuery.inputValue);
    setUrlSearchParams(newUrlSearchParams);
  };

  const fetchAlarms = async () => {
    if (isLoading) return;
    const itemQueryCount = itemQueryCountRef.current;
    try {
      setIsLoading(true);

      const payload = buildForensicsPayload(
        searchQuery.inputValue,
        itemQueryCount,
        searchQuery.selectedImage,
        searchQuery.filters
      );
      const res = await getSearchResults.mutateAsync(payload);
      setResults(res);
      setError(null);
      updateUrlSearchParams(searchQuery);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'An unknown error occurred'
      );
      if (itemQueryCount > 9) {
        itemQueryCountRef.current -= 9;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialUrlSearchParams = initialUrlSearchParamsRef.current;
    if (initialUrlSearchParams) {
      const urlQuery = initialUrlSearchParams.get('q') || '';
      if (urlQuery) {
        fetchAlarms();
      }
    }
  }, []);

  const updateSearchQuery = (newSearchQuery: SearchQuery) => {
    setSearchQuery(newSearchQuery);
  };

  const handleLoadMore = () => {
    itemQueryCountRef.current += 9;
    fetchAlarms();
  };

  const onSearch = () => {
    itemQueryCountRef.current = 9;
    fetchAlarms();
  };

  return (
    <Page title="Forensic Search" titleFontSize="large">
      <div
        id="error-validation-container"
        className="absolute left-1/3 top-2 z-20 h-10 w-[40rem] duration-300 ease-in-out"
      >
        {error && (
          <Alert
            type="error"
            size="small"
            closeable
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
      </div>
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 px-4 py-1">
        <ForensicsFilterSection
          searchQuery={searchQuery}
          updateSearchQuery={updateSearchQuery}
          isLoading={isLoading}
          onSearch={onSearch}
          setError={setError}
        />
        <ForensicsMessageSection isLoading={isLoading} results={results} />
        <ForensicsAlarmsSection
          results={results}
          isLoading={isLoading}
          onLoadMore={handleLoadMore}
        />
      </div>
    </Page>
  );
};

export const ForensicsRoot = withAuthz(
  withErrorBoundary(ForensicsRootComponent),
  ['forensics:view']
);
